import { useEffect, useRef, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { MinusSignIcon, PlusSignIcon } from '@hugeicons/core-free-icons';
import { EXTRA_SEAT_MONTHLY_PRICE_USD, MAX_EXTRA_SEATS, type TeamBillingInfo } from '@overlayqa/shared';
import { Button } from '../ui/button';
import { capture } from '../../lib/analytics';
import { extraSeatsErrorCopy } from '../../lib/extraSeats';
import { useExtraSeats } from '../../hooks/useExtraSeats';

/**
 * Buy seats on top of the plan's bundle (Emily, 2026-10-07: $12 per user on
 * Starter and Team). The owner sets an ABSOLUTE count, so a double click or a
 * retry cannot buy twice. The count cannot step below the people already on
 * the team; the server refuses that too (SEATS_BELOW_MEMBERS).
 *
 * Shown only where a purchase can succeed: the server's
 * `extraSeatsAvailable` (released, Stripe payer, a plan that sells seats)
 * and the owner.
 */
export function ExtraSeatsControl({ billingInfo, autoFocus = false, onSavingChange }: {
  billingInfo: TeamBillingInfo;
  autoFocus?: boolean;
  onSavingChange?: (saving: boolean) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const savingRef = useRef(false);
  useEffect(() => { if (autoFocus) inputRef.current?.focus(); }, [autoFocus]);
  const saved = billingInfo.extraSeats ?? 0;
  const bundled = billingInfo.maxSeats - saved;
  const floor = Math.max(0, billingInfo.currentSeats - bundled);

  const { save, isSaving } = useExtraSeats();
  const [draft, setDraft] = useState(saved);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Follow the server once it answers (a refetch after save, or another tab).
  useEffect(() => setDraft(saved), [saved]);

  if (!billingInfo.isOwner || !billingInfo.extraSeatsAvailable) return null;

  const clamp = (n: number) => Math.min(MAX_EXTRA_SEATS, Math.max(floor, (Number.isFinite(n) ? Math.trunc(n) : floor)));
  const change = (n: number) => {
    setError(null);
    setNotice(null);
    setDraft(clamp(n));
  };

  const onSave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    onSavingChange?.(true);
    capture('extra_seats_update_clicked', { plan: billingInfo.plan, from: saved, to: draft });
    // A retry replaces the last outcome; leaving it up showed "Seats updated"
    // above a stale refusal (mini A/B frame 05, 2026-10-07).
    setError(null);
    setNotice(null);
    try {
      const result = await save(draft);
      if (result.ok) {
        setNotice(`Seats updated. Your team now has ${result.maxSeats} seats.`);
      } else {
        setError(extraSeatsErrorCopy(result.code, result.message));
      }
    } finally {
      savingRef.current = false;
      onSavingChange?.(false);
    }
  };

  return (
    <section id="billing-extra-seats" aria-labelledby="extra-seats-heading" className="mt-4 border-t border-gray-100 pt-4">
      <h3 id="extra-seats-heading" className="text-sm text-gray-500">Extra seats</h3>
      <p className="mt-1 text-sm text-gray-600" style={{ textWrap: 'pretty' }}>
        Your plan includes {bundled} {bundled === 1 ? 'seat' : 'seats'}. Extra seats are ${EXTRA_SEAT_MONTHLY_PRICE_USD} per user per month.
      </p>

      <p className="mt-3 text-sm font-medium text-gray-900" aria-live="polite">{bundled + draft} seats total · ${draft * EXTRA_SEAT_MONTHLY_PRICE_USD}/month extra</p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="secondary"
            size="icon"
            aria-label="Remove a seat"
            disabled={draft <= floor || isSaving}
            onClick={() => change(draft - 1)}
          >
            <HugeiconsIcon icon={MinusSignIcon} size={16} strokeWidth={2} color="currentColor" aria-hidden="true" />
          </Button>
          <input
            ref={inputRef}
            type="number"
            aria-label="Extra seats"
            className="input billing-seat-number w-16 text-center"
            min={floor}
            max={MAX_EXTRA_SEATS}
            value={draft}
            disabled={isSaving}
            onChange={(e) => change(Number(e.target.value))}
          />
          <Button
            type="button"
            variant="secondary"
            size="icon"
            aria-label="Add a seat"
            disabled={draft >= MAX_EXTRA_SEATS || isSaving}
            onClick={() => change(draft + 1)}
          >
            <HugeiconsIcon icon={PlusSignIcon} size={16} strokeWidth={2} color="currentColor" aria-hidden="true" />
          </Button>
        </div>
        <Button type="button" size="sm" isLoading={isSaving} disabled={draft === saved} onClick={() => void onSave()}>
          Update seats
        </Button>
      </div>

      {draft > saved && (
        <p className="mt-2 text-sm text-gray-500">Added seats are charged now for the rest of this billing period.</p>
      )}
      {notice && (
        <p role="status" className="mt-2 text-sm text-green-700">
          {notice}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
    </section>
  );
}
